import React, { useEffect, useState } from "react";
import { NextPage } from "next";
import { useRouter } from "next/router";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import getDispensaryReviews from "api/requests/getDispensaryReviews";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { Dispensary } from "custom-types/Dispensary";
import { ConsumerDispensaryReview } from "custom-types/Reviews";
import { COMPLIANCE_RULESET_LOAD } from "redux/reducers/complianceRules";
import { getDomainCountryCode } from "redux/selectors/config";
import logError from "utils/logError";
import { getFirstQueryParam } from "utils/nextRouterUtils";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";
import { validateDispensary } from "utils/validateDispensary";

import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeaderSlim from "components/Dispensary/DispensaryHeaderSlim/DispensaryHeaderSlim";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import ReviewContainer, {
  ReviewType,
} from "components/Dispensary/Reviews/components/ReviewContainer";
import ReviewsHeader from "components/Dispensary/ReviewsHeader";
import PageNavigation from "components/PageNavigation";
import Placeholder, { Rect } from "components/Placeholder";
import Sort from "components/Sort";

import DispensaryBreadcrumbs from "../../../components/Dispensary/DispensaryBreadcrumbs";

type ReviewPageProps = {
  dispensary: Dispensary;
};
const DISPENSARY_REVIEWS_PER_PAGE = 24;

/* eslint-disable sort-keys-fix/sort-keys-fix -- exception: order important to display */
const sortOptions: { [key: string]: string } = {
  "Most Helpful": "upvotes_count:desc",
  "Least Helpful": "upvotes_count:asc",
  Newest: "created:desc",
  Oldest: "created:asc",
};
/* eslint-enable sort-keys-fix/sort-keys-fix -- exception: order important to display */

const ReviewsPage: NextPage<ReviewPageProps> = ({ dispensary }) => {
  const router = useRouter();

  const page = Math.max(Number(getFirstQueryParam(router.query.page) ?? 1), 1);
  const sort =
    getFirstQueryParam(router.query.sort) ?? sortOptions["Most Helpful"];

  const [loading, setLoading] = useState<boolean>(true);
  const [dispensaryReviews, setDispensaryReviews] = useState<
    ConsumerDispensaryReview[]
  >([]);
  const [reviewCount, setReviewCount] = useState(0);
  const { featureTier } = dispensary;
  const isInfoTier = featureTier === "info";

  useTrackDispensaryPageViewSplitEvent({ page: "reviews", tier: featureTier });

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);

      const { reviews, totalReviews } = await getDispensaryReviews(
        dispensary.slug,
        (page - 1) * DISPENSARY_REVIEWS_PER_PAGE,
        DISPENSARY_REVIEWS_PER_PAGE,
        encodeURI(sort)
      );

      setDispensaryReviews(reviews);
      setReviewCount(totalReviews);
      setLoading(false);
    };
    fetchReviews();
  }, [sort, page, dispensary.slug]);

  return (
    <>
      <DispensaryHead dispensary={dispensary} pageType="reviews" />
      <DispensaryHeaderSlim reviewCount={reviewCount} isInfoTier={isInfoTier} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.reviews}
        isInfoTier={isInfoTier}
      />
      <section className="container mt-xl lg:mt-section">
        <ReviewsHeader />
      </section>
      <section className="container flex flex-col justify-end mb-section mt-xl">
        {(dispensaryReviews.length || loading) && (
          <Sort className="self-end mb-sm" sortOptions={sortOptions} />
        )}
        <span>
          {!loading ? (
            dispensaryReviews.map((review) => (
              <ReviewContainer
                review={review}
                key={review.id}
                type={ReviewType.DISPENSARY}
              />
            ))
          ) : (
            <div className="mb-section">
              <Placeholder width="100%" height="270" className="mb-lg">
                <Rect width="100%" height="100%" />
              </Placeholder>
              <Placeholder width="100%" height="270" className="mb-lg">
                <Rect width="100%" height="100%" />
              </Placeholder>
              <Placeholder width="100%" height="270" className="mb-lg">
                <Rect width="100%" height="100%" />
              </Placeholder>
            </div>
          )}
        </span>
        <PageNavigation
          currentPage={page}
          totalPages={Math.ceil(reviewCount / DISPENSARY_REVIEWS_PER_PAGE)}
        />
        <DispensaryBreadcrumbs />
      </section>
      <style jsx>{`
        .review-image {
          max-height: 220px;
        }
      `}</style>
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
ReviewsPage.getInitialProps = async (context): Promise<any> => {
  const { req, res, query, store } = context;
  const { slug } = query;

  const countryCode = getDomainCountryCode(store.getState());

  try {
    const dispensary = await getDispensary(slug as string);

    const { redirect } = await validateDispensary({
      countryCode,
      dispensary,
      query,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      req: req!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      res: res!,
    });
    if (redirect) {
      return;
    }

    if (dispensary) {
      store.dispatch({
        dispensary,
        type: "dispensary/load",
      });

      const complianceRuleset = await getRegionComplianceRules(
        dispensary?.state,
        dispensary?.country
      );
      if (complianceRuleset) {
        store.dispatch({
          complianceRuleset,
          type: COMPLIANCE_RULESET_LOAD,
        });
      }

      return {
        dispensary,
      };
    } else if (context.res) {
      context.res.statusCode = 404;
      return;
    }
  } catch (err) {
    logError(
      "Error processing data in /dispensary-info/[slug]/reviews getInitialProps"
    );

    return;
  }
};

export default ReviewsPage;
